import * as React from 'react'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { toast } from '@sales-center/components'

import { addPaymentMethod } from '@user/store/payments/slice'
import { ENTITY_TYPES } from '@sales-center/common'
import { incrementLeadPaymentsCount } from '@user/store/lead/api'
import { incrementOpportunityPaymentsCount } from '@user/store/opportunity/api'
import {
  isPaymentCallbackError,
  isPaymentCallbackSuccess,
  PaymentCallbackResponse,
} from './typings'
import { PaymentMethod } from '@user/store/payments/typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { sendPaymentNotification } from '@user/store/payments'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  onClose: () => void
  url: string
}

export const Embed: FC<Props> = (props: Props) => {
  const { entityId, entityType, onClose, url } = props
  const [isListening, setIsListening] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const hostedPaymentsCallback = useCallback(
    (event: MessageEvent) => {
      if (!mounted.current) return

      const data: PaymentCallbackResponse = JSON.parse(event.data)

      if (isPaymentCallbackError(data)) {
        console.error(data.Error)
        onClose()
      }

      if (isPaymentCallbackSuccess(data)) {
        const method: PaymentMethod = {
          accountNumber: data.CreditCard.AccountNumber,
          accountType: data.AccountType,
          cardToken: data.CreditCard.CardToken,
          creditCardType: data.CreditCard.CreditCardType,
          expirationDate: data.CreditCard.ExpirationDate,
          id: data.CardID.toString(),
          isPrimary: data.IsPrimary,
          nameOnCard: data.CreditCard.NameOnCard,
        }
        toast.success('Successfully added payment method.')
        dispatch(addPaymentMethod(method))
        dispatch(
          sendPaymentNotification({
            entityType: entityType,
            entityId: entityId,
            last4DigitsOfCardOnFile: method.accountNumber,
          }),
        )

        if (entityType == ENTITY_TYPES.Lead) {
          dispatch(incrementLeadPaymentsCount(entityId))
        } else {
          dispatch(incrementOpportunityPaymentsCount(entityId))
        }
        onClose()
      }
    },
    [dispatch, entityId, entityType, onClose],
  )

  useEffect(() => {
    if (mounted.current && !isListening) {
      window.addEventListener('message', hostedPaymentsCallback, false)
      setIsListening(true)
    }

    return () => {
      if (!mounted.current) {
        window.removeEventListener('message', hostedPaymentsCallback)
      }
    }
  }, [hostedPaymentsCallback, isListening])

  return <iframe src={url} height="100%" width="100%" frameBorder="0"></iframe>
}

Embed.displayName = 'Embed'
